import { useState, useMemo, lazy, Suspense } from 'react'

import { Search, ChevronDown, ChevronRight } from 'lucide-react'
import * as stylex from '@stylexjs/stylex'
import { styles } from '@/styles/SoundEffectPage.stylex'

const SoundEffectPopupRaw = lazy(() => import('@/components/popup/SoundEffectPopup'));
const SoundEffectPopup = (props: any) => (
    <Suspense fallback={null}><SoundEffectPopupRaw {...props} /></Suspense>
);

function SoundEffectGalleryItemComponent({ item, onOpenPopup }: any) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            {...stylex.props(
                styles.soundEffectGalleryItem,
                isHovered && styles.soundEffectItemHovered
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div {...stylex.props(styles.soundEffectInfoContainer)}>
                <div {...stylex.props(styles.soundEffectPrompt)}>{item.prompt}</div>
                <div
                    {...stylex.props(
                        styles.clickIndicatorSoundEffect,
                        isHovered && styles.clickIndicatorVisible
                    )}
                    onClick={() => onOpenPopup(item)}
                >
                    Click Me!
                </div>
            </div>

            <div {...stylex.props(styles.audioPlayerContainer)} onClick={(e) => e.stopPropagation()}>
                <audio controls {...stylex.props(styles.audioPlayer)}>
                    <source src={item.audioSrc} type="audio/mpeg" />
                </audio>
            </div>
        </div>
    )
}

export function CategorizedSoundEffectGallery({ title, description, modelKey, modelLabel, modelNote, categories }: any) {
    const [searchQuery, setSearchQuery] = useState('')
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
    const [selectedItem, setSelectedItem] = useState<any>({ id: '', audioSrc: '', prompt: '' })
    const [showPopup, setShowPopup] = useState(false)

    const toggleCategory = (name: string) => {
        const next = new Set(expandedCategories);
        next.has(name) ? next.delete(name) : next.add(name);
        setExpandedCategories(next);
    }

    const filtered = useMemo(() => {
        const query = searchQuery.toLowerCase();
        return categories.map((cat: any) => ({
            ...cat,
            items: cat.items.filter((i: any) => i.prompt.toLowerCase().includes(query))
        })).filter((cat: any) => cat.items.length > 0);
    }, [categories, searchQuery]);

    const handleOpenPopup = (item: any) => {
        setSelectedItem(item);
        setShowPopup(true);
    };

    return (
        <>
            <div {...stylex.props(styles.gallerySection)}>
                <h3 {...stylex.props(styles.sectionTitle)}>{title}</h3>
                <p {...stylex.props(styles.galleryDescription)} dangerouslySetInnerHTML={{ __html: description }} />

                <div {...stylex.props(styles.searchContainer)}>
                    <div {...stylex.props(styles.searchInputWrapper)}>
                        <Search size={20} {...stylex.props(styles.searchIcon)} />
                        <input
                            type="text"
                            placeholder="Search sound effects..."
                            {...stylex.props(styles.searchInput)}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>

                <div {...stylex.props(styles.categoriesContainer)}>
                    {filtered.map((cat: any) => (
                        <div key={cat.name} {...stylex.props(styles.categoryItem)}>
                            <div {...stylex.props(styles.categoryHeader)} onClick={() => toggleCategory(cat.name)}>
                                <div {...stylex.props(styles.categoryName)}>
                                    {expandedCategories.has(cat.name) ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                                    <span>{cat.name}</span>
                                    <span {...stylex.props(styles.itemCount)}>({cat.items.length})</span>
                                </div>
                            </div>
                            {expandedCategories.has(cat.name) && (
                                <div {...stylex.props(styles.categoryContent)}>
                                    <div {...stylex.props(styles.soundEffectsList)}>
                                        {cat.items.map((item: any, idx: number) => (
                                            <SoundEffectGalleryItemComponent
                                                key={idx}
                                                item={item}
                                                onOpenPopup={handleOpenPopup}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <SoundEffectPopup
                isOpen={showPopup}
                onClose={() => setShowPopup(false)}
                {...selectedItem}
                modelKey={modelKey}
                modelLabel={modelLabel}
                modelNote={modelNote}
            />
        </>
    )
}